import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIsPresent } from 'framer-motion'
import * as Sentry from '@sentry/electron/renderer'
import { useHistoryPreview, useMessageListScrollActions, useSubmitMessage } from '@renderer/chat'
import { useModels } from '@renderer/chat/store/models'
import { useAvailableSkills } from '@renderer/chat/hooks/useSkills'
import { toSkillMention } from '@renderer/chat/components/chat/input/mentions'
import { useStagedActionsAdmin } from '../../hooks/use-staged-actions'
import { useViewActions, useReplaceView, useUpdateTopViewContext } from '../../view-atoms'
import { shouldShowChatMessageListSkeleton } from '../../components/chat/chat-message-list-skeleton'
import { useChatIdReconciliation } from './use-chat-id-reconciliation'
import { useChatStreamingLifecycle } from './use-chat-streaming-lifecycle'
import { useChatScrollTargets } from './use-chat-scroll-targets'
import { useVisibleChatPreview } from './use-visible-chat-preview'
import { useComposerDraft, type ComposerDraft } from './use-composer-draft'

export interface ChatViewContext {
  chatId?: string
  title?: string
  isNew?: boolean
  instantScrollToBottom?: boolean
  /**
   * Scroll to (and surface) a specific message once the conversation loads —
   * used to jump straight to a background AskUserQuestion in its chat context.
   */
  scrollToMessageId?: string
  /**
   * Changes on every open so reopening the same question (same id, mounted
   * view) still re-triggers the scroll. See getQuestionChatTarget.
   */
  scrollNonce?: number
  /**
   * When this chat was opened for a background question, the question's staged
   * action id. If the conversation can't be loaded, the view falls back to the
   * Action View for this action. See getQuestionChatTarget.
   */
  questionActionId?: string
  /**
   * Set when the view is opened by a fresh local submit (e.g. a brief submit)
   * that already owns an in-flight submitMessage stream. Suppresses the
   * streaming verify/re-subscribe so it doesn't clobber that stream.
   */
  isFreshSubmit?: boolean
  /**
   * Whether a pending AskUserQuestion/AskUserFeedback is waiting on the user
   * in this conversation. Mirrored from the staged-actions cache by the view;
   * gates the floating question card so an idle chat doesn't reserve
   * floating-bar room.
   */
  hasPendingQuestions?: boolean
  /** Keys a new chat opened to send `initialPrompt`, so each send gets its own window. */
  draftId?: string
  /** Sent once by the view when it mounts as a new chat. */
  initialPrompt?: string
  /** The model `initialPrompt` goes to. Its text @-mentions it. */
  initialPromptModelId?: string
  /** The skill `initialPrompt` invokes. Its text names it after a `/`. */
  initialPromptSkillId?: string
  /** Sends `initialPrompt` with what's attached to the composer, so the new-chat reset leaves it in place. */
  sendsComposerAttachments?: boolean
  /** Left in the composer, unsent, while the view is a new chat. */
  composerDraft?: ComposerDraft
}

export interface ChatViewModel {
  chatTitle: string
  showSuggestions: boolean
  activeChatId: string | undefined
  shareChatId: string | undefined
  showScrollToEnd: boolean
  /** True while the skeleton should hold: fetch pending, or a large chat still sliding in. */
  isLoadingMessages: boolean
  scrollToEnd: ReturnType<typeof useMessageListScrollActions>['scrollToEnd']
  onStagedActionClick: (actionId: string) => void
  onStagedActionCreated: () => void
  onReminderClick: (reminderId: string) => void
  onSuggestionClick: (prompt: string) => void
}

/** How long "loaded, and still empty" must hold before the view gives up on a question's chat. */
const QUESTION_FALLBACK_DELAY_MS = 1500

/**
 * The Chat View's behavior, composed from three hooks that own one concern
 * each: id reconciliation, the streaming relationship, and scroll targeting.
 * What's left here is the view's own surface — its title, its outbound
 * navigation, and the one policy that belongs to neither of the three: giving
 * up on a question's chat that can't be loaded.
 *
 * The chat UI itself is untouched; this only feeds it.
 */
export function useChatViewModel(context: ChatViewContext | undefined, entranceSettled: boolean): ChatViewModel {
  const { openView } = useViewActions()
  // Every fact reconciled below (the real conversation id, isNew, isFreshSubmit)
  // is about *this* mounted chat. Patching by id instead would stamp the adopted
  // id onto every buried chat entry, so Back would land on the wrong conversation.
  const updateChatContext = useUpdateTopViewContext('chat')
  const replaceView = useReplaceView()
  const { data: historyData } = useHistoryPreview()
  const { invalidateStagedActions } = useStagedActionsAdmin()
  const submitMessage = useSubmitMessage()
  const isNewChat = context?.isNew ?? false

  const { activeChatId, messageCount, currentChatId, resolvedChatId, isFetched, isFetching, loadedMessageCount } =
    useChatIdReconciliation(context, updateChatContext)

  // The flag lives in the persisted view context, so it must be cleared here
  // at its source once the local submit's stream settles.
  const onFreshSubmitSettled = useCallback(
    () => updateChatContext((prev) => (prev?.isFreshSubmit ? { ...prev, isFreshSubmit: false } : prev)),
    [updateChatContext],
  )
  useChatStreamingLifecycle({
    chatId: currentChatId,
    resolvedChatId,
    isNew: isNewChat,
    isFreshSubmit: context?.isFreshSubmit ?? false,
    onFreshSubmitSettled,
  })
  useVisibleChatPreview({
    conversationId: resolvedChatId,
    entranceSettled,
    hasMessages: messageCount > 0,
  })

  const isLoadingMessages = shouldShowChatMessageListSkeleton({
    messageCount,
    isFetched,
    entranceSettled,
  })
  const scroll = useChatScrollTargets(context, !isLoadingMessages, messageCount)

  // Live history (IPC-synced metadata) wins over the title stamped into view
  // context at open — otherwise a rename in another window never reaches this header.
  const chatTitle = useMemo(() => {
    if (isNewChat && !activeChatId) return 'New Chat'
    const chatId = activeChatId ?? context?.chatId
    const fromHistory = chatId && historyData ? historyData.find((c) => c.id === chatId)?.title : undefined
    return fromHistory || context?.title || 'Chat'
  }, [isNewChat, context?.title, context?.chatId, activeChatId, historyData])

  const handleStagedActionClick = useCallback(
    (actionId: string) => {
      openView('action', { actionId }, { sourceLabel: 'Chat' })
    },
    [openView],
  )

  const handleReminderClick = useCallback(
    (reminderId: string) => {
      openView('reminder', { reminderId }, { sourceLabel: 'Chat' })
    },
    [openView],
  )

  const handleSuggestionClick = useCallback(
    (prompt: string) => {
      submitMessage(prompt)
    },
    [submitMessage],
  )

  const initialPrompt = isNewChat ? context?.initialPrompt : undefined
  const initialPromptModel = useModels().find((model) => model.id === context?.initialPromptModelId)
  const initialPromptSkill = useAvailableSkills().find((skill) => skill.id === context?.initialPromptSkillId)
  const initialPromptMentions = useMemo(
    () => [
      ...(initialPromptModel ? [initialPromptModel] : []),
      ...(initialPromptSkill ? [toSkillMention(initialPromptSkill)] : []),
    ],
    [initialPromptModel, initialPromptSkill],
  )
  const sendsComposerAttachments = context?.sendsComposerAttachments ?? false
  const sentInitialPromptRef = useRef(false)
  const submitMessageRef = useRef(submitMessage)
  submitMessageRef.current = submitMessage
  useEffect(() => {
    if (!initialPrompt || sentInitialPromptRef.current) {
      return
    }
    // Deferred past the new-chat reset (which clears the active chat, and runs twice under
    // StrictMode) so the submit's active chat survives and the view follows it.
    const timer = setTimeout(() => {
      sentInitialPromptRef.current = true
      updateChatContext((prev) => (prev?.initialPrompt ? { ...prev, initialPrompt: undefined } : prev))
      void submitMessageRef
        .current(initialPrompt, {
          isNewChat: true,
          mentions: initialPromptMentions,
          preserveDraft: true,
          // Otherwise the composer is the user's own, holding whatever they had there.
          ...(sendsComposerAttachments ? {} : { attachments: [], preserveComposerContext: true }),
        })
        .catch((error: unknown) => Sentry.captureException(error))
    }, 0)
    return () => clearTimeout(timer)
  }, [initialPrompt, initialPromptMentions, sendsComposerAttachments, updateChatContext])

  const dropComposerDraftText = useCallback(
    () =>
      updateChatContext((prev) =>
        prev?.composerDraft?.text ? { ...prev, composerDraft: { ...prev.composerDraft, text: undefined } } : prev,
      ),
    [updateChatContext],
  )
  // An exiting view stays mounted after the next one is on top, and that one
  // can make its own chat active; neither may read as this chat's send.
  const isTop = useIsPresent()
  // The view mounts, or comes back on top, while another chat is still active,
  // so only a send after the new-chat reset counts.
  const isUnsent = isNewChat && !activeChatId
  const wasUnsentRef = useRef(false)
  useEffect(() => {
    if (!isTop) {
      wasUnsentRef.current = false
      return
    }
    if (isUnsent) {
      wasUnsentRef.current = true
      return
    }
    if (!wasUnsentRef.current) {
      return
    }
    wasUnsentRef.current = false
    // Sent, so returning before the chat's id lands must not attach the draft again.
    updateChatContext((prev) => (prev?.composerDraft ? { ...prev, composerDraft: undefined } : prev))
  }, [isTop, isUnsent, updateChatContext])
  useComposerDraft(isTop && isUnsent ? context?.composerDraft : undefined, dropComposerDraftText)

  // Fallback for a background question whose chat can't be loaded (deleted or
  // otherwise missing — fetchChatMessages swallows the error and returns no
  // messages). A question's conversation always has at least the asking
  // message, so a settled-empty *fetch* means the chat is unusable: drop back to
  // the Action View rather than dead-end on a blank chat. We key on the
  // fetched result, not the atom — stale cached messages for this chatId would
  // otherwise mask an empty API response and suppress the fallback. The timer
  // only fires if "settled + empty" holds continuously (the dep-change cleanup
  // cancels it the moment a fetch/refetch starts or returns messages), so it
  // rides over the view's one-shot refetch without firing prematurely.
  const questionActionId = context?.questionActionId
  useEffect(() => {
    if (!questionActionId) return
    if (!entranceSettled || !isFetched || isFetching || loadedMessageCount > 0) return
    const timer = setTimeout(() => {
      // Swap only the failed chat view for the Action View — any parent below
      // it (e.g. the Actions list) stays reachable via back.
      replaceView('chat', { id: 'action', context: { actionId: questionActionId, kind: 'question' as const } })
    }, QUESTION_FALLBACK_DELAY_MS)
    return () => clearTimeout(timer)
  }, [questionActionId, entranceSettled, isFetched, isFetching, loadedMessageCount, replaceView])

  const shareChatId = activeChatId ?? context?.chatId

  return {
    chatTitle,
    showSuggestions: isNewChat && !activeChatId,
    activeChatId,
    shareChatId,
    isLoadingMessages,
    ...scroll,
    onStagedActionClick: handleStagedActionClick,
    onStagedActionCreated: invalidateStagedActions,
    onReminderClick: handleReminderClick,
    onSuggestionClick: handleSuggestionClick,
  }
}
